import { BalanceData, PET_ABILITY_SLOTS, PetAbilityKind, PetAbilitySlot, PetData, PlayerProfile, UpgradeJob, UpgradeTarget, isQueued, nextUpgradeLevel, petRule } from "@highmarch/domain";
import { PetAbilityNumbers, abilityNumbers } from "./petAbilityNumbers";

export type { PetAbilityNumbers, PetEffectNumbers } from "./petAbilityNumbers";
export { abilityNumbers, effectNumbers } from "./petAbilityNumbers";
import { petIndex } from "./pets";

/**
 * The pet sheet's view model (Pets v2, docs/PETS.md): the pet's level and cap, its next upgrade (gold, timer, what is
 * in the way), and each ability's numbers at this level and the next, or the level it unlocks at. Display numbers
 * (seconds, cells) are plain numbers for the presentation; the rules are the domain's (meta/petAbilities.ts petRule,
 * meta/profile.ts costFor / durationFor / upgradeCap).
 */

export interface PetAbilityLine {
  /** Its slot (attack, passive, pulse, signature) and its kind (bite, strike, … : `now.id` / `next.id` when shown). */
  readonly id: PetAbilitySlot;
  readonly kind: PetAbilityKind;
  readonly name: string;
  readonly fromLevel: number;
  /** Unlocked at the pet's level now. */
  readonly unlocked: boolean;
  /** Its numbers now (null while locked) and at the next level (null at the max, or still locked then). */
  readonly now: PetAbilityNumbers | null;
  readonly next: PetAbilityNumbers | null;
}

/** The next upgrade: running or queued (more may queue while under the cap), ready, or held by the max / the cap / not owning the pet. */
export type PetUpgradeState = "ready" | "running" | "queued" | "cap" | "max" | "notOwned";

export interface PetSheet {
  readonly pet: PetData;
  /** 0 while not owned. */
  readonly level: number;
  readonly cap: number;
  readonly maxLevel: number;
  readonly equipped: boolean;
  readonly upgrade: {
    readonly state: PetUpgradeState;
    /** The level the next upgrade reaches (after what is running and queued), its gold and timer, and whether the gold is there. */
    readonly toLevel: number;
    readonly cost: number;
    readonly seconds: number;
    readonly affordable: boolean;
    readonly job: UpgradeJob | null;
  };
  readonly abilities: readonly PetAbilityLine[];
}

/** The pet sheet for `pet` on this profile (`equippedId`: the pet coming into battles, or null). */
export function petSheet(balance: BalanceData, profile: PlayerProfile, pet: PetData, equippedId: string | null): PetSheet {
  const i = petIndex(balance, pet.id), level = profile.pets.levelOf(pet.id), max = balance.tables.maxLevel, cap = profile.petLevelCap;
  const job = profile.jobs.find((j) => j.target === UpgradeTarget.Pet && j.towerIndex === i) ?? null;
  const toLevel = level > 0 ? nextUpgradeLevel(profile, UpgradeTarget.Pet, i) : 1;
  const cost = level > 0 && toLevel <= max ? profile.costFor(toLevel) : 0;
  const state: PetUpgradeState = level === 0 ? "notOwned" : job !== null ? "running" : isQueued(profile, UpgradeTarget.Pet, i) ? "queued" : toLevel > max ? "max" : toLevel > cap ? "cap" : "ready";
  const shown = Math.max(1, level), now = petRule(balance, pet, shown), next = shown < max ? petRule(balance, pet, shown + 1) : null;
  const abilities = PET_ABILITY_SLOTS.map((id): PetAbilityLine => {
    const a = pet.abilities[id];
    return { id, kind: a.kind, name: a.name, fromLevel: a.fromLevel, unlocked: level >= a.fromLevel, now: level >= a.fromLevel ? abilityNumbers(now, id) : null, next: next === null ? null : abilityNumbers(next, id) };
  });
  return {
    pet, level, cap, maxLevel: max, equipped: equippedId === pet.id,
    upgrade: { state, toLevel, cost, seconds: level > 0 && toLevel <= max ? profile.durationFor(toLevel) : 0, affordable: profile.canAfford(cost), job },
    abilities,
  };
}
